import { AlertTriangle, CircleCheck, FilterX, Loader2, RadioTower, Search } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';

type ErrorStateProperties = {
  message: string;
  onRetry?: () => void;
};

export type StateTone = 'coverage' | 'error' | 'filtered' | 'healthy' | 'neutral' | 'stale';

type StateViewProperties = {
  action?: ReactNode;
  children?: ReactNode;
  title: string;
  tone?: StateTone;
};

const toneContract: Record<StateTone, { icon: LucideIcon; iconClass: string; panelClass: string }> = {
  coverage: {
    icon: RadioTower,
    iconClass: 'text-info',
    panelClass: 'border-info/25 bg-info-wash',
  },
  error: {
    icon: AlertTriangle,
    iconClass: 'text-critical',
    panelClass: 'border-critical/25 bg-critical-wash',
  },
  filtered: {
    icon: FilterX,
    iconClass: 'text-warning',
    panelClass: 'border-warning/25 bg-warning-wash',
  },
  healthy: {
    icon: CircleCheck,
    iconClass: 'text-healthy',
    panelClass: 'border-healthy/25 bg-healthy-wash',
  },
  neutral: {
    icon: Search,
    iconClass: 'text-muted',
    panelClass: 'border-line bg-panel',
  },
  stale: {
    icon: AlertTriangle,
    iconClass: 'text-warning',
    panelClass: 'border-warning/25 bg-warning-wash',
  },
};

export const StateView = ({ action, children, title, tone = 'neutral' }: StateViewProperties) => {
  const contract = toneContract[tone];
  const Icon = contract.icon;

  return (
    <Card className={contract.panelClass}>
      <CardContent className="text-muted grid justify-items-center gap-2 px-5 py-10 text-center text-sm">
        <span className="bg-raised/70 grid size-9 place-items-center rounded-full">
          <Icon aria-hidden="true" className={`size-5 ${contract.iconClass}`} />
        </span>
        <p className="text-ink font-semibold">{title}</p>
        {children ? <div className="max-w-xl text-balance">{children}</div> : null}
        {action ? <div className="mt-2">{action}</div> : null}
      </CardContent>
    </Card>
  );
};

export const LoadingState = ({ label = 'Loading data' }: { label?: string }) => (
  <Card aria-live="polite" role="status">
    <CardContent className="text-muted grid gap-3 text-sm">
      <div className="flex items-center gap-3">
        <Loader2 aria-hidden="true" className="text-accent size-4 animate-spin" />
        {label}
      </div>
      <div className="grid gap-2">
        <Skeleton className="h-3 w-2/3" />
        <Skeleton className="h-3 w-1/2" />
      </div>
    </CardContent>
  </Card>
);

export const ErrorState = ({ message, onRetry }: ErrorStateProperties) => (
  <Card aria-live="assertive" className="border-critical/25 bg-critical-wash" role="alert">
    <CardContent className="flex flex-wrap items-center justify-between gap-3 text-sm">
      <div className="text-critical flex items-center gap-3">
        <AlertTriangle aria-hidden="true" className="size-4" />
        <span>{message}</span>
      </div>
      {onRetry ? (
        <Button onClick={onRetry} variant="secondary">
          Retry
        </Button>
      ) : null}
    </CardContent>
  </Card>
);

export const EmptyState = ({ children, title, tone = 'neutral' }: { children?: ReactNode; title: string; tone?: StateTone }) => (
  <StateView title={title} tone={tone}>
    {children}
  </StateView>
);
